import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db, collection, getDocs, query, orderBy, doc, deleteDoc, updateDoc, where } from '../services/firebase';
import './AdminOrdersPage.css';

const AdminOrdersPage = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showDetails, setShowDetails] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!currentUser || !currentUser.isAdmin) {
          setError('You do not have permission to view this page.');
          setLoading(false);
          return;
        }
        const ordersRef = query(collection(db, 'orders'), orderBy(sortBy, sortOrder));
        const ordersSnap = await getDocs(ordersRef);
        setOrders(ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        setError('Failed to fetch orders.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [currentUser, sortBy, sortOrder]);

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) {
      return;
    }

    setDeleting(orderId);
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      setOrders(orders.filter(order => order.id !== orderId));
    } catch (err) {
      console.error('Error deleting order:', err);
      alert('Failed to delete order.');
    } finally {
      setDeleting(null);
    }
  };

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), { status });
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status } : order
      ));
    } catch (err) {
      console.error('Error updating order status:', err);
      alert('Failed to update order status.');
    }
  };
  
  const handleBulkAction = async () => {
    if (!bulkAction || selectedOrders.size === 0) return;

    const selectedOrderIds = Array.from(selectedOrders);
    
    if (bulkAction === 'delete') {
      if (!window.confirm(`Delete ${selectedOrderIds.length} selected orders?`)) return;
      
      try {
        await Promise.all(
          selectedOrderIds.map(orderId => deleteDoc(doc(db, 'orders', orderId)))
        );
        setOrders(orders.filter(order => !selectedOrderIds.includes(order.id)));
        setSelectedOrders(new Set());
      } catch (err) {
        alert('Failed to delete orders.');
      }
    } else if (bulkAction === 'paid' || bulkAction === 'completed' || bulkAction === 'pending') {
      try {
        await Promise.all(
          selectedOrderIds.map(orderId => 
            updateDoc(doc(db, 'orders', orderId), { status: bulkAction })
          )
        );
        setOrders(orders.map(order => 
          selectedOrderIds.includes(order.id) 
            ? { ...order, status: bulkAction }
            : order
        ));
        setSelectedOrders(new Set());
      } catch (err) {
        alert('Failed to update order status.');
      }
    }
    setBulkAction('');
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return "";
    if (typeof dateObj === 'object' && dateObj.seconds) {
      return new Date(dateObj.seconds * 1000).toLocaleDateString();
    }
    return new Date(dateObj).toLocaleDateString();
  };

  const formatDateTime = (dateObj) => {
    if (!dateObj) return "";
    const date = typeof dateObj === 'object' && dateObj.seconds 
      ? new Date(dateObj.seconds * 1000)
      : new Date(dateObj);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => `QAR ${(amount || 0).toFixed(2)}`;

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', class: 'status-pending' },
      paid: { label: 'Paid', class: 'status-paid' },
      completed: { label: 'Completed', class: 'status-completed' }
    };
    return statusMap[status] || { label: 'Unknown', class: 'status-unknown' };
  };

  const getContextualActions = (order) => {
    const actions = [];
    
    switch (order.status) {
      case 'pending':
        actions.push({
          label: 'Mark Paid',
          action: () => handleUpdateStatus(order.id, 'paid'),
          type: 'primary',
          icon: '💳'
        });
        break;
      case 'paid':
        actions.push({
          label: 'Complete',
          action: () => handleUpdateStatus(order.id, 'completed'),
          type: 'success',
          icon: '✅'
        });
        actions.push({
          label: 'Refund',
          action: () => handleRefund(order.id),
          type: 'warning',
          icon: '↩️'
        });
        break;
      case 'completed':
        actions.push({
          label: 'Refund',
          action: () => handleRefund(order.id),
          type: 'warning',
          icon: '↩️'
        });
        break;
    }
    
    actions.push({
      label: 'Delete',
      action: () => handleDeleteOrder(order.id),
      type: 'danger',
      icon: '🗑️'
    });
    
    return actions;
  };

  const getNextStepAction = (order) => {
    switch (order.status) {
      case 'pending':
        return {
          label: 'Mark Paid',
          action: () => handleUpdateStatus(order.id, 'paid'),
          type: 'primary'
        };
      case 'paid':
        return {
          label: 'Complete',
          action: () => handleUpdateStatus(order.id, 'completed'),
          type: 'success'
        };
      default:
        return null;
    }
  };

  const handleRefund = async (orderId) => {
    if (!window.confirm('Process refund for this order?')) return;
    
    try {
      await updateDoc(doc(db, 'orders', orderId), { 
        status: 'refunded',
        refundedAt: new Date()
      });
      setOrders(orders.map(order => 
        order.id === orderId 
          ? { ...order, status: 'refunded', refundedAt: new Date() }
          : order
      ));
    } catch (err) {
      console.error('Error processing refund:', err);
      alert('Failed to process refund.');
    }
  };

  const getOrderItems = (order) => {
    if (!order.items || order.items.length === 0) return 'No items';
    if (order.items.length === 1) return order.items[0].name;
    if (order.items.length === 2) return `${order.items[0].name}, ${order.items[1].name}`;
    return `${order.items[0].name} +${order.items.length - 1} more`;
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'today') {
      const today = new Date().toDateString();
      const orderDate = order.createdAt?.seconds 
        ? new Date(order.createdAt.seconds * 1000).toDateString()
        : new Date(order.createdAt).toDateString();
      return orderDate === today;
    }
    if (filter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const orderDate = order.createdAt?.seconds 
        ? new Date(order.createdAt.seconds * 1000)
        : new Date(order.createdAt);
      return orderDate >= weekAgo;
    }
    if (filter === 'overdue') {
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
      const orderDate = order.createdAt?.seconds 
        ? new Date(order.createdAt.seconds * 1000)
        : new Date(order.createdAt);
      return order.status === 'pending' && orderDate < twoDaysAgo;
    }
    return order.status === filter;
  });

  const toggleOrderSelection = (orderId) => {
    const newSelection = new Set(selectedOrders);
    if (newSelection.has(orderId)) {
      newSelection.delete(orderId);
    } else {
      newSelection.add(orderId);
    }
    setSelectedOrders(newSelection);
  };

  const toggleAllOrders = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(order => order.id)));
    }
  };

  const toggleOrderDetails = (orderId) => {
    const newDetails = new Set(showDetails);
    if (newDetails.has(orderId)) {
      newDetails.delete(orderId);
    } else {
      newDetails.add(orderId);
    }
    setShowDetails(newDetails);
  };

  if (loading) return <div className="admin-loading">Loading orders...</div>;
  if (error) return <div className="admin-error">{error}</div>;

  return (
    <div className="admin-orders-dashboard">
      {/* Header with KPIs */}
      <div className="orders-header">
        <h1>Orders Management</h1>
        <div className="orders-kpis">
          <div className="kpi-card">
            <div className="kpi-value">{orders.length}</div>
            <div className="kpi-label">Total Orders</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-value">{orders.filter(o => o.status === 'pending').length}</div>
            <div className="kpi-label">Pending Payment</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-value">{orders.filter(o => o.status === 'paid').length}</div>
            <div className="kpi-label">Ready to Ship</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-value">{formatCurrency(orders.reduce((sum, o) => sum + (o.total || 0), 0))}</div>
            <div className="kpi-label">Total Revenue</div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="orders-toolbar">
        <div className="toolbar-row">
          <div className="toolbar-left">
            <div className="filter-group">
              <div className="quick-filters">
                <button 
                  className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button 
                  className={`filter-btn ${filter === 'today' ? 'active' : ''}`}
                  onClick={() => setFilter('today')}
                >
                  Today
                </button>
                <button 
                  className={`filter-btn ${filter === 'week' ? 'active' : ''}`}
                  onClick={() => setFilter('week')}
                >
                  This Week
                </button>
                <button 
                  className={`filter-btn ${filter === 'overdue' ? 'active' : ''}`}
                  onClick={() => setFilter('overdue')}
                >
                  Overdue
                </button>
              </div>
            </div>
          </div>
          
          {selectedOrders.size > 0 && (
            <div className="toolbar-right">
              <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                <option value="">Bulk Actions ({selectedOrders.size})</option>
                <option value="paid">Mark as Paid</option>
                <option value="completed">Mark as Completed</option>
                <option value="pending">Mark as Pending</option>
                <option value="delete">Delete</option>
              </select>
              <button onClick={handleBulkAction} className="apply-bulk-btn">
                Apply
              </button>
            </div>
          )}
        </div>
        
        <div className="toolbar-row">
          <div className="toolbar-left">
            <div className="filter-group">
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All Orders</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="overdue">Overdue</option>
                <option value="pending">Pending Payment</option>
                <option value="paid">Ready to Ship</option>
                <option value="completed">Completed</option>
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="createdAt">Sort by Date</option>
                <option value="total">Sort by Amount</option>
                <option value="status">Sort by Status</option>
              </select>
              <button 
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="sort-toggle"
              >
                {sortOrder === 'desc' ? '↓' : '↑'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>
                <input 
                  type="checkbox" 
                  checked={selectedOrders.size === filteredOrders.length && filteredOrders.length > 0}
                  onChange={toggleAllOrders}
                />
              </th>
              <th>Order ID</th>
              <th>Date/Time</th>
              <th>Customer</th>
              <th>Items</th>
              <th>Total</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map(order => (
              <React.Fragment key={order.id}>
                <tr className={`order-row ${selectedOrders.has(order.id) ? 'selected' : ''}`}>
                  <td>
                    <input 
                      type="checkbox" 
                      checked={selectedOrders.has(order.id)}
                      onChange={() => toggleOrderSelection(order.id)}
                    />
                  </td>
                  <td>
                    <div className="order-id-cell">
                      <span className="order-id">{order.orderReference || order.id.slice(-8)}</span>
                      <button 
                        className="details-toggle"
                        onClick={() => toggleOrderDetails(order.id)}
                      >
                        {showDetails.has(order.id) ? '−' : '+'}
                      </button>
                    </div>
                  </td>
                  <td className="datetime-cell">{formatDateTime(order.createdAt)}</td>
                  <td>
                    <div className="customer-cell">
                      <div className="customer-name">{order.userName || order.shipping?.fullName || "N/A"}</div>
                      <div className="customer-phone">{order.phone || order.shipping?.phone || "No phone"}</div>
                    </div>
                  </td>
                  <td>
                    <div 
                      className="items-preview"
                      onClick={() => toggleOrderDetails(order.id)}
                      style={{cursor: 'pointer'}}
                    >
                      <span className="items-text" title={order.items?.map(item => `${item.name} (${item.quantity || 1})`).join(', ')}>
                        {getOrderItems(order)}
                      </span>
                      <span className="items-count">{order.items?.length || 0}</span>
                      <span className="expand-icon">{showDetails.has(order.id) ? '▼' : '▶'}</span>
                    </div>
                  </td>
                  <td className="total-cell">{formatCurrency(order.total)}</td>
                  <td>
                    <span className={`status-badge ${getStatusBadge(order.status).class}`}>
                      {getStatusBadge(order.status).label}
                    </span>
                  </td>
                  <td>
                    <div className="action-cell">
                      {(() => {
                        const nextStep = getNextStepAction(order);
                        if (nextStep) {
                          return (
                            <button 
                              className={`next-step-btn ${nextStep.type}`}
                              onClick={nextStep.action}
                              disabled={deleting === order.id}
                            >
                              {deleting === order.id ? '...' : nextStep.label}
                            </button>
                          );
                        }
                        return (
                          <div className="actions-dropdown">
                            <button className="dropdown-trigger">⋯</button>
                            <div className="dropdown-menu">
                              {getContextualActions(order).map((action, idx) => (
                                <button 
                                  key={idx}
                                  className={`dropdown-item ${action.type}`}
                                  onClick={action.action}
                                >
                                  <span className="action-icon">{action.icon}</span>
                                  {action.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
                {showDetails.has(order.id) && (
                  <tr className="order-details-row">
                    <td colSpan="9">
                      <div className="order-details">
                        <div className="details-section">
                          <h4>Items ({order.items?.length || 0})</h4>
                          <div className="items-list">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="item-detail">
                                <span className="item-name">{item.name}</span>
                                <span className="item-specs">
                                  {item.selectedWeight || item.weight || '250g'} 
                                  {item.selectedBeanType && ` • ${item.selectedBeanType}`}
                                </span>
                                <span className="item-price">
                                  {formatCurrency(item.price)} × {item.quantity || 1}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="details-section">
                          <h4>Customer & Delivery</h4>
                          <div className="delivery-info">
                            <p><strong>Phone:</strong> {order.phone || order.shipping?.phone || "N/A"}</p>
                            <p><strong>Email:</strong> {order.userEmail || order.shipping?.email || "N/A"}</p>
                            <p><strong>Method:</strong> {order.deliveryMethod === 'pickup' ? 'Pickup' : 'Delivery'}</p>
                            {order.deliveryMethod === 'pickup' && order.pickupBranch && (
                              <p><strong>Branch:</strong> {order.pickupBranch.replace('_', ' ')}</p>
                            )}
                            {order.shipping?.address && (
                              <p><strong>Address:</strong> {order.shipping.address}</p>
                            )}
                            <p><strong>Payment:</strong> {order.paymentMethod || "N/A"}</p>
                            <p><strong>Delivery Fee:</strong> {order.deliveryFee === 0 ? 'Free' : formatCurrency(order.deliveryFee)}</p>
                          </div>
                        </div>
                        
                        <div className="details-section">
                          <h4>Order Summary</h4>
                          <div className="order-summary">
                            <div className="summary-row">
                              <span>Subtotal:</span>
                              <span>{formatCurrency(order.subtotal)}</span>
                            </div>
                            <div className="summary-row">
                              <span>Delivery Fee:</span>
                              <span>{order.deliveryFee === 0 ? 'Free' : formatCurrency(order.deliveryFee)}</span>
                            </div>
                            <div className="summary-row total">
                              <span>Total:</span>
                              <span>{formatCurrency(order.total)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        {filteredOrders.length === 0 && (
          <div className="no-orders">
            <p>No orders found matching the current filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrdersPage; 